import CreditsBadge, { CreditType } from 'components/CreditsBadge';
import { ComponentType, forwardRef, HTMLAttributes, ReactNode } from 'react';
import { cn } from 'utils/style-utils';

/**
 * Either only the credits left, for any type, or how many the enrichment uses.
 * AI credits are charged by usage, so they have no count to show.
 */
export type EnrichRowCredits =
  | { type: CreditType; showOnlyLeft: true }
  | { type: Exclude<CreditType, 'rewrite'>; needed: number };

export interface EnrichRowProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  'title'
> {
  icon: ComponentType<{ className?: string }>;
  title: string;
  /** An id to label controls. */
  titleId?: string;
  subtitle?: string;
  /** Data of the credits slot. */
  credits?: EnrichRowCredits;
  /** The row's end slot. */
  end?: ReactNode;
  /** Extra details slot under the row. */
  details?: ReactNode;
}

/**
 * Card layout shared by the enrichments of the creation forms: icon tile and
 * title, then the credits and the row's control. Extra props reach the card,
 * so it can render as a menu trigger.
 */
const EnrichRow = forwardRef<HTMLDivElement, EnrichRowProps>(
  (
    {
      icon: Icon,
      title,
      titleId,
      subtitle,
      credits,
      end,
      details,
      className,
      ...props
    },
    ref,
  ) => (
    <div
      ref={ref}
      className={cn(
        'flex flex-col gap-0.5 p-1.5 rounded-lg border border-neutral-300 bg-white',
        className,
      )}
      {...props}
    >
      <div className="flex items-center gap-4 py-1.5 pl-1.5 pr-4.5">
        <div className="min-w-0 flex-1 flex items-center gap-4">
          <span className="shrink-0 flex p-2 rounded-md border border-neutral-300 bg-white">
            <Icon className="size-6" />
          </span>
          <div className="min-w-0 flex flex-col">
            <span
              id={titleId}
              className="truncate text-sm leading-6 text-gray-900"
            >
              {title}
            </span>
            {subtitle && (
              <span className="truncate text-xs text-gray-700">{subtitle}</span>
            )}
          </div>
        </div>
        <div className="shrink-0 flex items-center gap-4">
          {credits && (
            <CreditsBadge
              creditsType={credits.type}
              // Showing only those left, the badge has no count to show.
              creditsNeededForAction={'needed' in credits ? credits.needed : 0}
              showOnlyLeft={'showOnlyLeft' in credits}
            />
          )}
          {end}
        </div>
      </div>
      {details}
    </div>
  ),
);

EnrichRow.displayName = 'EnrichRow';

export default EnrichRow;
